import React from 'react';
import {
  Box, Typography, Button, TextField, IconButton, Divider,
  FormControl, List, MenuItem, Select,
} from '@mui/material';
import {
  Star as StarIcon, ArrowForward as ArrowForwardIcon,
  Lock as LockIcon, ShoppingCart as ShoppingCartIcon,
} from '@mui/icons-material';
import FAQAccordion from '../../../FAQAccordion';
import ComparisonTable from '../../../ComparisonTable';
import TwoColumnComparisonTable from '../../../TwoColumnComparisonTable';
import FittableImage from '../../../FittableImage';
import CircularImageCropper from '../../../CircularImageCropper';
import ImageCropper from '../../../ImageCropper';
import Checklist from '../../../Checklist';
import CenteredVideoPlayer from '../../../CenteredVideoPlayer';
import ResizableButton from '../../../ResizableButton';
import InlineRowLayout from '../../../InlineRowLayout';
import ExtractedElementControls from '../../../ElementControls';
import { getPreset, getPresetFontFamilies } from '../../../../textStyle.js';
import { getListIcon } from '../../../../utils/offerListIcons';
import { ELEMENT_TYPES, LAYOUT_TYPES } from '../../../../utils/offerElementTypes';
import { LAYOUT_CONFIGS } from '../../../../utils/offerLayoutConfigs';
import { calculateVideoAspectRatio } from '../../../../utils/offerPreviewElement';

export const renderLayoutElements = (element, ctx) => {
  const {
    isEditing, isEditingField, handleDoubleClick,
    isElementSelected, getSelectionStyles, ElementControls,
    editingElement, editingField, tempValue, setTempValue, textEditorRef,
    selectedElement, selectedElementContext, selectedSubElement,
    iconSelectorOpen, setIconSelectorOpen, isBrandsPage, paginationEnabled,
    currentPageIndex, pageSettings, forceRender, rightPanelOpen,
    setSelectedElement, setSelectedElementContext, setSelectedSubElement,
    setTabValue, setRightPanelOpen, setPages, setElements,
    setInsertAfterElementId, setPromptDialogTitle, setPromptDialogMessage,
    setPromptDialogValue, setPromptAction, setShowPromptDialog,
    startInlineEdit, saveInlineEdit, cancelInlineEdit, handleKeyPress,
    handleResizeStart, findAndUpdateLayout, moveElement, generateId,
    deleteElement, deleteElementFromZone, updateElement, applyHighlight,
    handleDragOver, handleDragLeave, handleDrop,
    getZoneHeightStyle, getHeightModeText, toggleZoneHeight, getHeightTooltip,
    getExpandIcon, selectedVariantKey, setSelectedVariantKey, targetZone,
    isInZone, layoutId, zoneId, elementIndex,
    renderInlineEditableElement,
  } = ctx;

  switch (element.type) {
case ELEMENT_TYPES.TEXT_WITH_RIGHT_IMAGE:
  return (
    <Box
      sx={{
        ...element.styles,
        mb: 2,
        position: 'relative',
        '&:hover .element-controls': { opacity: 1 },
        ...getSelectionStyles(element.id)
      }}
      onClick={(e) => {
        e.stopPropagation();
        console.log('🔴 Text with Right Image clicked - selecting element:', element.id);
        setSelectedElement(element);
        setSelectedElementContext({ isInZone, layoutId, zoneId });
        setTabValue(1); // Set to Style tab
        setRightPanelOpen(true); // Ensure right panel is open
      }}
      title="Click to select and edit styles • Double-click to edit content"
    >
      <ElementControls />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
        {/* Text Content */}
        <Box sx={{ flex: 1 }}>
          {isEditingField('content.text') ? (
            <TextField
              value={tempValue}
              onChange={(e) => setTempValue(e.target.value)}
              onKeyDown={handleKeyPress}
              onBlur={saveInlineEdit}
              autoFocus
              variant="outlined"
              size="small"
              fullWidth
              multiline
              rows={2}
            />
          ) : (
            <Box
              sx={{
                cursor: 'pointer',
                padding: '8px 12px',
                borderRadius: '4px',
                border: '1px solid transparent',
                position: 'relative',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  border: '1px solid rgba(25, 118, 210, 0.3)',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                },
                transition: 'all 0.2s ease',
              }}
              onDoubleClick={() => handleDoubleClick('content.text', element.content.text)}
              title="Double-click to edit text"
            >
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'inherit' }}>
                {element.content.text}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Image */}
        <Box sx={{ flexShrink: 0, width: '180px' }}>
          {isEditingField('content.image') ? (
            <TextField
              value={tempValue}
              onChange={(e) => setTempValue(e.target.value)}
              onKeyDown={handleKeyPress}
              onBlur={saveInlineEdit}
              autoFocus
              variant="outlined"
              size="small"
              fullWidth
              placeholder="Enter image URL"
            />
          ) : (
            <Box
              sx={{
                cursor: 'pointer',
                position: 'relative',
                '&:hover': {
                  '& .image-overlay': {
                    opacity: 1
                  }
                }
              }}
              onDoubleClick={() => handleDoubleClick('content.image', element.content.image)}
              title="Double-click to edit image URL"
            >
              <img
                src={element.content.image}
                alt="Right side image"
                style={{
                  width: '100%',
                  height: '120px',
                  objectFit: 'cover',
                  borderRadius: '8px',
                  border: '1px solid #e0e0e0'
                }}
                onError={(e) => {
                  if (e.target.src !== 'https://via.placeholder.com/180x120?text=Image') {
                    e.target.src = 'https://via.placeholder.com/180x120?text=Image';
                  }
                }}
              />


            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );

case ELEMENT_TYPES.LEFT_IMAGE_TEXT:
  const currentAlignment = element.imageAlignment === 'top' ? 'flex-start' :
    element.imageAlignment === 'bottom' ? 'flex-end' : 'center';

  return (
    <Box
      sx={{
        ...element.styles,
        mb: 2,
        position: 'relative',
        '&:hover .element-controls': { opacity: 1 },
        ...getSelectionStyles(element.id)
      }}
      onClick={(e) => {
        e.stopPropagation();
        console.log('🔴 Left Image + Text clicked - selecting element:', element.id);
        setSelectedElement(element);
        setSelectedElementContext({ isInZone, layoutId, zoneId });
        setTabValue(1); // Set to Style tab
        setRightPanelOpen(true); // Ensure right panel is open
      }}
      title="Click to select and edit styles • Double-click to edit content"
    >
      <ElementControls />
      <Box sx={{
        ...element.styles,
        // Override with specific alignment - this must come after element.styles
        display: 'flex',
        alignItems: currentAlignment,
        gap: `${element.spacing || 20}px`
      }}>
        {/* Circular Image */}
        <Box sx={{
          flexShrink: 0,
          width: `${element.imageSize || 120}px`,
          height: `${element.imageSize || 120}px`
        }}>
          {isEditingField('content.image') ? (
            <TextField
              value={tempValue}
              onChange={(e) => setTempValue(e.target.value)}
              onKeyDown={handleKeyPress}
              onBlur={saveInlineEdit}
              autoFocus
              variant="outlined"
              size="small"
              fullWidth
              placeholder="Enter image URL"
              sx={{
                width: `${element.imageSize || 120}px`,
                '& .MuiOutlinedInput-root': {
                  height: '40px'
                }
              }}
            />
          ) : (
            <Box
              sx={{
                cursor: 'pointer',
                position: 'relative',
                width: `${element.imageSize || 120}px`,
                height: `${element.imageSize || 120}px`,
                '&:hover': {
                  '& .image-overlay': {
                    opacity: 1
                  }
                }
              }}
              onDoubleClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleDoubleClick('content.image', element.content.image);
              }}
              title="Double-click to edit image URL"
            >
              <img
                src={element.content.image}
                alt="Profile image"
                style={{
                  width: `${element.imageSize || 120}px`,
                  height: `${element.imageSize || 120}px`,
                  objectFit: 'cover',
                  borderRadius: '50%',
                  border: '3px solid #e0e0e0',
                  display: 'block'
                }}
                onError={(e) => {
                  if (e.target.src !== 'https://via.placeholder.com/150x150?text=Profile') {
                    e.target.src = 'https://via.placeholder.com/150x150?text=Profile';
                  }
                }}
                onLoad={(e) => {
                  // Prevent ResizeObserver errors on image load
                  e.target.style.opacity = '1';
                }}
              />


            </Box>
          )}
        </Box>

        {/* Text Content */}
        <Box sx={{ flex: 1, minHeight: '56px' }}>
          {isEditingField('content.text') ? (
            <TextField
              value={tempValue}
              onChange={(e) => setTempValue(e.target.value)}
              onKeyDown={handleKeyPress}
              onBlur={saveInlineEdit}
              autoFocus
              variant="outlined"
              size="small"
              fullWidth
              multiline
              rows={3}
              sx={{
                '& .MuiOutlinedInput-root': {
                  minHeight: '56px'
                }
              }}
            />
          ) : (
            <Box
              sx={{
                cursor: 'pointer',
                padding: '8px 12px',
                borderRadius: '4px',
                border: '1px solid transparent',
                position: 'relative',
                minHeight: '56px',
                display: 'flex',
                alignItems: 'center',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  border: '1px solid rgba(25, 118, 210, 0.3)',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                },
                transition: 'all 0.2s ease',
              }}
              onDoubleClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleDoubleClick('content.text', element.content.text);
              }}
              title="Double-click to edit text"
            >
              <Typography variant="body1" sx={{
                color: element.textStyles?.color || 'inherit',
                lineHeight: element.textStyles?.lineHeight || 1.6,
                flex: 1,
                fontSize: element.textStyles?.fontSize || '16px',
                fontWeight: element.textStyles?.fontWeight || 'normal',
                fontFamily: element.textStyles?.fontFamily || 'inherit',
                textAlign: element.textStyles?.textAlign || 'left',
                backgroundColor: element.textStyles?.backgroundColor || 'transparent',
                padding: element.textStyles?.padding || '0px',
                borderRadius: element.textStyles?.borderRadius || '0px'
              }}>
                {element.content.text}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );

case ELEMENT_TYPES.LAYOUT:
  const config = LAYOUT_CONFIGS[element.layoutType];

  // Special handling for INLINE_ROW layout
  if (element.layoutType === LAYOUT_TYPES.INLINE_ROW) {
    return (
      <Box
        data-element-id={element.id}
        sx={{
          position: 'relative',
          '&:hover .element-controls': { opacity: 1 },
          ...getSelectionStyles(element.id)
        }}
      >
        <ElementControls />
        <InlineRowLayout
          element={element}
          updateElement={updateElement}
          renderInlineEditableElement={renderInlineEditableElement}
          isInZone={isInZone}
          layoutId={layoutId}
          zoneId={zoneId}
          handleDragOver={handleDragOver}
          handleDragLeave={handleDragLeave}
          handleDrop={handleDrop}
          isBrandsPage={isBrandsPage}
        />
      </Box>
    );
  }

  return (
    <Box
      data-element-id={element.id}
      sx={{
        backgroundColor: element.styles?.backgroundColor || 'transparent',
        border: element.styles?.border || '1px dashed #ccc',
        borderRadius: element.styles?.borderRadius || 1,
        margin: element.styles?.margin || '0 0 8px 0',
        position: 'relative',
        '&:hover .element-controls': { opacity: 1 },
        ...element.styles,
        p: 0, // Force zero padding - override any saved padding
        ...getSelectionStyles(element.id)
      }}
      onClick={(e) => {
        e.stopPropagation();
        console.log('🔴 Layout clicked - selecting element:', element.id);
        setSelectedElement(element);
        setSelectedElementContext({ isInZone, layoutId, zoneId });
        setTabValue(1); // Set to Style tab
        setRightPanelOpen(true); // Ensure right panel is open
      }}
      title="Click to select layout"
    >
      <ElementControls />
      <Typography variant="body2" color="text.secondary" gutterBottom>
        📐 Layout: {config?.name}
      </Typography>
      <Box sx={{
        display: 'flex',
        gap: 0,
        flexWrap: element.layoutType === LAYOUT_TYPES.THREE_COLUMN ? 'wrap' : 'nowrap'
      }}>
        {element.zones.map((zone, zoneIndex) => (
          <Box
            key={zone.id}
            data-zone-id={zone.id}
            sx={{
              width: zone.width,
              border: '1px dashed #ccc', // Default border
              borderRadius: 1,
              p: 0,
              backgroundColor: pageSettings.backgroundColor,
              textAlign: zone.textAlign || 'left',
              position: 'relative',
              overflow: 'visible', // Let content fit height without scrolling
              ...getZoneHeightStyle(zone.id, zone.height || '60px') // Apply after so it can override border
            }}
            onDragOver={(e) => {
              console.log('🎯 Zone onDragOver triggered', { layoutId: element.id, zoneId: zone.id });
              handleDragOver(e, { layoutId: element.id, zoneId: zone.id });
            }}
            onDragLeave={(e) => {
              console.log('🎯 Zone onDragLeave triggered');
              handleDragLeave();
            }}
            onDrop={(e) => {
              console.log('🎯 Zone onDrop triggered', { elementId: element.id, zoneId: zone.id });
              handleDrop(e, element.id, zone.id);
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Zone {zoneIndex + 1} ({zone.elements.length} items){getHeightModeText(zone.id)}
              </Typography>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleZoneHeight(zone.id);
                }}
                title={getHeightTooltip(zone.id)}
                sx={{
                  fontSize: '12px',
                  minWidth: '20px',
                  width: '20px',
                  height: '20px',
                  backgroundColor: 'rgba(25, 118, 210, 0.1)',
                  '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.2)' }
                }}
              >
                {getExpandIcon(zone.id)}
              </IconButton>
            </Box>

            {zone.elements.length === 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Drop elements here
              </Typography>
            )}
            {zone.elements.map((zoneElement, elemIndex) => (
              <div key={zoneElement.id}>
                {renderInlineEditableElement(zoneElement, true, element.id, zone.id, elemIndex)}
              </div>
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  );
    default:
      return null;
  }
};
